/**
 * T2 Business Dashboard — the supplier ring.
 *
 * Two nested rings, as the supplied design reference draws them (Supply Chain Dashboard v5): the
 * inner ring is material type, the outer is that type's top ten suppliers with the rest folded into
 * one "Other" arc. The reading sits in the hole rather than in a tooltip that would cover the ring
 * it describes, and the legend beside it carries the names, values, shares and SDP.
 *
 * **The ranking is per material type**, decided in SQL. Fabric mills and trim suppliers trade in
 * different units and different orders of magnitude, so one combined ranking let accessories take
 * every slot and left fabric as a single "Other". Folding is destructive and depends on the
 * selected measure, so it happens in ADB and never here ([DATA-1]).
 *
 * Colour is identity, not grading: a hue says fabric or trims, and the shade says rank within that
 * class. The reference's palette lives in `index.css` as `--t2d-*`; arcs take the mark value and
 * legend text takes the `-ink` variant, which is legible as type on white.
 *
 * The panel is deliberately tight around the ring — the reference gives the chart the room and
 * spends none of it on padding.
 */

import { useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';

import { ChartContainer, type ChartConfig } from '@/components/ui/chart';
import { cn } from '@/lib/utils';

import {
  measureFigure,
  measureLabel,
  meetsTarget,
  orderStateWord,
  pct,
  type SupplierRingRow,
} from './contract';

/** Inner ring: material type. Outer: that type's suppliers. Tight radii keep the hole readable. */
const CLASS_RING = { inner: '34%', outer: '54%' } as const;
const SUPPLIER_RING = { inner: '57%', outer: '82%' } as const;

/** How far a segment fades while another one is being read. */
const DIMMED = 0.28;

/** Darkest at the top of the ranking, so order is visible in the ring itself. */
const SHADE = [1, 0.92, 0.84, 0.76, 0.7, 0.64, 0.58, 0.52, 0.47, 0.42] as const;

/** The folded remainder is neutral: it is not a supplier, it is everyone else. */
const OTHER_SHADE = 0.3;

type Hue = 'good' | 'blue' | 'violet';

function hueOf(materialClass: string | null): Hue {
  if (materialClass === 'Fabric') return 'good';
  if (materialClass === 'Accessories') return 'blue';
  return 'violet';
}

/**
 * The arc's fill. A CSS variable in an SVG paint attribute, which is how `chartColor` reaches
 * Recharts too — a colour never goes in an inline `style` (§4.8).
 *
 * The RAW token, not the `--color-` alias: `@theme inline` substitutes those into utility classes
 * at build time and never emits them as custom properties, so `var(--color-t2d-blue)` resolves to
 * nothing and SVG falls back to black. `chartColor` names `var(--chart-1)` for the same reason.
 */
function arcFill(hue: Hue): string {
  return `var(--t2d-${hue})`;
}

/**
 * The legend swatch, as static classes. Tailwind cannot build a class name at runtime, and the
 * shade ladder has to match the arc opacity beside it, so the steps are written out.
 */
const SWATCH: Record<Hue, readonly string[]> = {
  good: [
    'bg-t2d-good', 'bg-t2d-good/90', 'bg-t2d-good/85', 'bg-t2d-good/75', 'bg-t2d-good/70',
    'bg-t2d-good/65', 'bg-t2d-good/60', 'bg-t2d-good/50', 'bg-t2d-good/45', 'bg-t2d-good/40',
  ],
  blue: [
    'bg-t2d-blue', 'bg-t2d-blue/90', 'bg-t2d-blue/85', 'bg-t2d-blue/75', 'bg-t2d-blue/70',
    'bg-t2d-blue/65', 'bg-t2d-blue/60', 'bg-t2d-blue/50', 'bg-t2d-blue/45', 'bg-t2d-blue/40',
  ],
  violet: [
    'bg-t2d-violet', 'bg-t2d-violet/90', 'bg-t2d-violet/85', 'bg-t2d-violet/75', 'bg-t2d-violet/70',
    'bg-t2d-violet/65', 'bg-t2d-violet/60', 'bg-t2d-violet/50', 'bg-t2d-violet/45', 'bg-t2d-violet/40',
  ],
};

const OTHER_SWATCH: Record<Hue, string> = {
  good: 'bg-t2d-good/30',
  blue: 'bg-t2d-blue/30',
  violet: 'bg-t2d-violet/30',
};

interface Arc {
  key: string;
  name: string;
  value: number;
  hue: Hue;
  opacity: number;
  swatch: string;
  row: SupplierRingRow;
  /** A folded "Other <class>" arc rather than a named supplier. */
  folded: boolean;
}

function shadeFor(rank: number | null): number {
  if (typeof rank !== 'number' || rank < 1) return OTHER_SHADE;
  return SHADE[Math.min(rank, SHADE.length) - 1] ?? OTHER_SHADE;
}

function toArc(row: SupplierRingRow, index: number): Arc {
  const hue = hueOf(row.material_class);
  // A class row carries no rank because it is the parent ring, not a supplier outside the top N.
  // Reading its null rank as "folded" dimmed the whole inner ring to the Others shade.
  const folded = row.ring === 'supplier' && row.rank_no === null;
  const rank = row.rank_no;
  return {
    key: `${row.material_class ?? '?'}-${row.t2_group ?? 'other'}-${index}`,
    name: (row.t2_group ?? row.material_class ?? '—').replace(/\s+/g, ' ').trim(),
    value: Math.max(0, row.measure_value ?? 0),
    hue,
    opacity: folded ? OTHER_SHADE : row.ring === 'class' ? 1 : shadeFor(rank),
    swatch: folded
      ? OTHER_SWATCH[hue]
      : (SWATCH[hue][Math.min(rank ?? 1, SHADE.length) - 1] ?? SWATCH[hue][0]!),
    row,
    folded,
  };
}

/** SDP, tinted only when the one global target is met — see `cards.tsx` for why misses are plain. */
function Sdp({ row }: { row: SupplierRingRow }) {
  const met = meetsTarget(row.sdp_pct, row.sdp_on_time_qty, row.sdp_delivered_qty);
  return (
    <span
      className={cn(
        'w-10 shrink-0 text-right text-[11.5px] font-medium tabular-nums',
        met === true ? 'text-t2d-good-ink' : 'text-t2d-ink/70',
      )}
    >
      {met === null ? '—' : pct(row.sdp_pct, 0)}
    </span>
  );
}

const CONFIG: ChartConfig = { value: { label: 'Share' } };

export function SupplierRing({
  rows,
  measure,
  orderState,
  complete,
}: {
  rows: readonly SupplierRingRow[];
  measure: string;
  orderState: string;
  /**
   * The response was not capped. The ring is withheld entirely rather than drawn without its
   * percentages: an arc's angle *is* a share claim, so stripping the printed figures would leave
   * the shape making it anyway (§4.3).
   */
  complete: boolean;
}) {
  const [hovered, setHovered] = useState<string | null>(null);

  const title = `${measureLabel(measure)} ${orderStateWord(orderState)} by material type and supplier`;
  const classArcs = rows.filter((row) => row.ring === 'class').map(toArc);
  const supplierArcs = rows.filter((row) => row.ring === 'supplier').map(toArc);
  const total = classArcs.reduce((sum, arc) => sum + arc.value, 0);

  if (rows.length === 0) {
    return (
      <section aria-label={title} className="flex min-w-0 flex-col gap-2.5 px-6 py-4">
        <Head title={title} topN={namedPerClass(rows)} />
        <p className="py-6 text-sm text-t2d-muted">
          Nothing was ordered in this period. Widen the dates, or clear a filter.
        </p>
      </section>
    );
  }

  if (!complete) {
    return (
      <section aria-label={title} className="flex min-w-0 flex-col gap-2.5 px-6 py-4">
        <Head title={title} topN={namedPerClass(rows)} />
        <p className="py-6 text-sm text-t2d-muted">
          The supplier breakdown came back incomplete. Every arc here is a share of the whole book,
          so the ring is held back rather than drawn as a share of the rows that loaded. Narrow the
          dates, or pick a material type.
        </p>
      </section>
    );
  }

  const active = [...classArcs, ...supplierArcs].find((arc) => arc.key === hovered);

  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-2 px-6 py-4">
      <Head title={title} topN={namedPerClass(rows)} />

      <div className="flex min-w-0 flex-col items-center gap-3 md:flex-row md:items-start">
        {/* The ring gets the room; the panel spends none of it on padding. */}
        <div className="relative w-full max-w-[360px] shrink-0 md:w-[52%]">
          <ChartContainer
            config={CONFIG}
            aria-label={title}
            className="aspect-square h-auto w-full [&_.recharts-sector]:outline-none"
          >
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={classArcs}
                dataKey="value"
                nameKey="name"
                innerRadius={CLASS_RING.inner}
                outerRadius={CLASS_RING.outer}
                isAnimationActive={false}
                stroke="var(--t2d-surface)"
                strokeWidth={1.5}
                onMouseEnter={(_d, index) => setHovered(classArcs[index]?.key ?? null)}
                onMouseLeave={() => setHovered(null)}
              >
                {classArcs.map((arc) => (
                  <Cell
                    key={arc.key}
                    fill={arcFill(arc.hue)}
                    fillOpacity={hovered !== null && hovered !== arc.key ? DIMMED : arc.opacity}
                  />
                ))}
              </Pie>
              <Pie
                data={supplierArcs}
                dataKey="value"
                nameKey="name"
                innerRadius={SUPPLIER_RING.inner}
                outerRadius={SUPPLIER_RING.outer}
                isAnimationActive={false}
                stroke="var(--t2d-surface)"
                strokeWidth={1.5}
                onMouseEnter={(_d, index) => setHovered(supplierArcs[index]?.key ?? null)}
                onMouseLeave={() => setHovered(null)}
              >
                {supplierArcs.map((arc) => (
                  <Cell
                    key={arc.key}
                    fill={arcFill(arc.hue)}
                    fillOpacity={hovered !== null && hovered !== arc.key ? DIMMED : arc.opacity}
                  />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>

          {/* Pointer events off, or the readout would take the hover it exists to report. */}
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5 px-14 text-center">
            <span className="max-w-full truncate text-[25px] leading-none font-semibold text-t2d-ink tabular-nums">
              {measureFigure(active === undefined ? total : active.value)}
            </span>
            <span className="max-w-full truncate text-[11px] tracking-[0.14em] text-t2d-muted uppercase">
              {active === undefined
                ? `${orderStateWord(orderState)} ${measure === 'value' ? 'HKD' : 'pcs'}`
                : active.name}
            </span>
          </div>
        </div>

        <ol className="flex min-w-0 flex-1 flex-col gap-[5px]">
          {classArcs.map((classArc) => (
            <li key={classArc.key} className="flex min-w-0 flex-col gap-[5px]">
              <LegendRow arc={classArc} isClass onHover={setHovered} />
              {supplierArcs
                .filter((arc) => arc.row.material_class === classArc.row.material_class)
                .map((arc) => (
                  <LegendRow key={arc.key} arc={arc} onHover={setHovered} />
                ))}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/**
 * How many suppliers the query named per class. It keeps five each when both classes are in the
 * answer and ten when only one is, so the sub-line reads the result rather than asserting a number
 * the query may not have used.
 */
function namedPerClass(rows: readonly SupplierRingRow[]): number {
  return rows.reduce((max, row) => Math.max(max, row.rank_no ?? 0), 0);
}

function Head({ title, topN }: { title: string; topN: number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <h3 className="text-[15px] font-semibold text-t2d-ink">{title}</h3>
      <p className="text-xs text-t2d-muted">
        inner ring: material type · outer ring: top {topN || 10} suppliers per type, then others ·
        last column: SDP
      </p>
    </div>
  );
}

function LegendRow({
  arc,
  isClass = false,
  onHover,
}: {
  arc: Arc;
  isClass?: boolean;
  onHover: (key: string | null) => void;
}) {
  return (
    <div
      onMouseEnter={() => onHover(arc.key)}
      onMouseLeave={() => onHover(null)}
      className={cn(
        'flex min-w-0 items-center gap-2',
        isClass ? 'border-t border-t2d-line-soft pt-[5px] first:border-t-0' : 'pl-4',
      )}
    >
      <span aria-hidden className={cn('size-[9px] shrink-0', arc.swatch)} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          isClass
            ? 'text-xs font-semibold tracking-[0.1em] text-t2d-ink uppercase'
            : 'text-[12.5px] text-t2d-ink/75',
        )}
      >
        {arc.name}
      </span>
      <span
        className={cn(
          'shrink-0 text-[12.5px] whitespace-nowrap tabular-nums',
          isClass ? 'font-semibold text-t2d-ink' : 'text-t2d-ink',
        )}
      >
        {measureFigure(arc.value)}
      </span>
      <span className="w-8 shrink-0 text-right text-[11.5px] text-t2d-faint tabular-nums">
        {pct(arc.row.share_pct, 0)}
      </span>
      <Sdp row={arc.row} />
    </div>
  );
}
